<template>
  <div class="rules">
    <TabsHead active-tab="rules"></TabsHead>
    <ViewHeaderBar>
      <template #left>
        <Button class="header-item btn" @click="isShowImportRulesModal(true)">
          {{ $t('rules.importRules') }}
        </Button>
        <Button :disabled="!isSelectedRules" class="header-item btn" @click="exportSelectedRules">
          {{ $t('rules.exportRules') }}
        </Button>
        <Button class="header-item btn" @click="clearRuleAlarms">{{ $t('rules.clearAlarm') }}</Button>
      </template>
      <template #right>
        <Button class="header-item btn" type="primary" @click="goToSubApp">Flow(beta)</Button>
        <Button class="header-item btn" type="primary" to="rules/0?oper=create">{{ $t('rules.createRule') }}</Button>
      </template>
    </ViewHeaderBar>

    <Table :loading="loading" :columns="recordsCol" :data="records" @on-selection-change="selectRuleChange"></Table>

    <Drawer
      v-model="statusVisisble"
      :title="$t('common.status')"
      :width="480"
      class="view-form status-drawer"
      closeable
    >
      <template v-if="typeof statusRecord !== 'string'">
        <div v-for="(value, name) in statusRecord" :key="(value, name)">
          <div class="view-item">
            <label class="view-item--label">{{ name }}: </label>
            <span>{{ value }}</span>
          </div>
        </div>
      </template>
      <div v-else>{{ statusRecord }}</div>
    </Drawer>

    <!-- copy rule -->
    <CopyRuleModal :modalVisible.sync="isCopyRuleModalVisible" :ruleID="copiedRuleId" @submited="submitedCopyRule" />

    <!-- import rules -->
    <ImportFileModel
      :modalVisible.sync="importRulesModalVisible"
      :isUploading="isImportingRules"
      @submit="importRules"
    />
  </div>
</template>

<script lang="jsx">
import { cloneDeep, maxBy } from 'lodash'
import TabsHead from '@/components/TabsHead'
import {
  loadRules,
  deleteRule,
  viewRule,
  viewRuleStatus,
  restartRule,
  startRule,
  stopRule,
  batchExportRules,
  batchImportRules,
} from '@/api/rules'
import nodeID from '@/mixins/nodeID'
import upload from '@/mixins/upload'
import EllipsisTooltip from '@/components/EllipsisTooltip.vue'
import { setClearRuleAlarmTime, getClearRuleAlarmTime } from '@/utils/localStorage'
import CopyRuleModal from './components/CopyRuleForm.vue'
import StatusDot from '@/components/StatusDot.vue'
import ImportFileModel from '@/components/ImportFileModel.vue'
import ViewHeaderBar from '@/components/ViewHeaderBar.vue'

export default {
  name: 'Rules',

  components: {
    TabsHead,
    EllipsisTooltip,
    CopyRuleModal,
    StatusDot,
    ImportFileModel,
    ViewHeaderBar,
  },

  mixins: [nodeID, upload],

  data() {
    return {
      timer: 0,
      runningRulesStatusTimer: null,
      statusVisisble: false,
      loading: false,
      statusRecord: {},
      records: [],
      currentRuleId: '',
      recordsCol: [
        {
          type: 'selection',
          width: 60,
          align: 'center',
        },
        {
          title: 'ID',
          key: 'id',
          render: (h, params) => {
            return (
              <div>
                <a
                  href="javascript:;"
                  onClick={() => {
                    this.showDetail(params.row, 'view')
                  }}
                >
                  {params.row.id}
                </a>
              </div>
            )
          },
        },
        {
          title: this.$t('common.name'),
          key: 'name',
          render: (h, params) => {
            return <span>{params.row.name}</span>
          },
        },
        {
          title: this.$t('common.status'),
          key: 'isRunning',
          render: (h, params) => {
            let statusType = this.isRuleRunning(params.row) ? 'running' : 'stoped'
            if (!this.isRuleRunning(params.row) && !this.isStopRuleSuccess(params.row)) {
              statusType = 'error'
            }
            const statusText = this.isRuleRunning(params.row) ? this.$t('common.running') : this.$t('common.stoped')
            return (
              <div
                class="status-wrap"
                onClick={() => {
                  this.showStatus(params.row)
                }}
              >
                <StatusDot
                  type={statusType}
                  text={statusText}
                  isLoading={this.isLoadingStatusSet[params.row.id]}
                  loadingText={this.loadingStatusText}
                />
                <Icon type="ios-information-circle-outline" class="status-detail" />
              </div>
            )
          },
        },
        {
          title: this.$t('rules.alarmCounts'),
          key: 'warning',
          render: (h, params) => {
            let total = 0
            if (params.row?.statusDetail) {
              total = this.getLatestExceptionData(params.row.statusDetail, 'total')
            }
            return <span>{total}</span>
          },
        },
        {
          title: this.$t('rules.lastAlarm'),
          key: 'warning',
          width: 170,
          render: (h, params) => {
            let latestException = ''
            if (params.row?.statusDetail) {
              latestException = this.getLatestExceptionData(params.row.statusDetail, 'lastException')
            }
            return <EllipsisTooltip text={latestException} />
          },
        },
        {
          title: this.$t('common.operations'),
          key: 'action',
          width: 260,
          render: (h, params) => {
            const icon = this.isRuleRunning(params.row) ? 'ios-pause' : 'ios-play'
            return (
              <div>
                <Tooltip content={this.$t('common.edit')} placement="top">
                  <a
                    href="javascript:;"
                    class="opertor-icon"
                    onClick={() => {
                      this.showDetail(params.row, 'edit')
                    }}
                  >
                    <Icon type="ios-create"></Icon>
                  </a>
                </Tooltip>
                <Tooltip
                  content={this.isRuleRunning(params.row) ? this.$t('common.stop') : this.$t('common.start')}
                  placement="top"
                >
                  <a
                    href="javascript:;"
                    class="opertor-icon"
                    onClick={() => {
                      this.toggleRuleStatus(params.row)
                    }}
                  >
                    <Icon type={icon} />
                  </a>
                </Tooltip>
                <Tooltip content={this.$t('common.restart')} placement="top">
                  <a
                    href="javascript:;"
                    class="opertor-icon"
                    onClick={() => {
                      this.restartRuleById(params.row)
                    }}
                  >
                    <Icon type="md-sync" />
                  </a>
                </Tooltip>
                <Tooltip content={this.$t('rules.topo')} placement="top">
                  <a
                    href="javascript:;"
                    class="opertor-icon"
                    onClick={() => {
                      this.showTopo(params.row)
                    }}
                  >
                    <Icon type="md-git-network" />
                  </a>
                </Tooltip>
                <Tooltip content={this.$t('common.copy')} placement="top">
                  <a
                    href="javascript:;"
                    class="opertor-icon"
                    onClick={() => {
                      this.showCopyRuleModal(params.row)
                    }}
                  >
                    <Icon type="ios-copy"></Icon>
                  </a>
                </Tooltip>
                <Tooltip content={this.$t('common.delete')} placement="top">
                  <a href="javascript:;">
                    <Poptip
                      confirm={true}
                      transfer={true}
                      width={200}
                      title={this.$t('common.confirmDelete')}
                      placement="top"
                      type="error"
                      vOn:on-ok={() => {
                        this.remove(params.row)
                      }}
                      vOn:on-cancel={() => {}}
                    >
                      <Icon type="md-trash"></Icon>
                    </Poptip>
                  </a>
                </Tooltip>
              </div>
            )
          },
        },
      ],
      isCopyRuleModalVisible: false,
      copiedRuleId: '',
      isLoadingStatusSet: {},
      loadingStatusText: '',
      selectedRules: [],
      importRulesModalVisible: false,
      isImportingRules: false,
    }
  },

  computed: {
    /**
     * get rule the latest Exception  data
     * @param {Object} ruleStatusDetailData
     * @param {String} type total | lastException
     */
    getLatestExceptionData() {
      return (ruleStatusDetailData, type) => {
        const keys = Object.keys(ruleStatusDetailData)
        const timeKey = 'last_exception_time'
        const allLastTimekeys = keys.filter((item) => item.includes(timeKey))

        if (!allLastTimekeys.length) return type === 'total' ? 0 : ''

        // get the lastest time key
        const lastestTimeKey = maxBy(allLastTimekeys, (key) => {
          const tm = ruleStatusDetailData[key]
          const timestamp = tm ? new Date(tm).getTime() : 0
          return timestamp
        })

        const lastExceptionTime = new Date(ruleStatusDetailData[lastestTimeKey]).getTime()
        const clearAlarmLastTime = getClearRuleAlarmTime()

        // last Exception time < clear rule alarm time: clear all rule alarm
        if (lastExceptionTime < clearAlarmLastTime) return type === 'total' ? 0 : ''

        // get the total exceptions key && the latest exception at the latest time
        const exceptionKeyPrefix = String(lastestTimeKey).split(timeKey)[0]
        const totalExceptionsKey = `${exceptionKeyPrefix}exceptions_total`
        const lastExceptionKey = `${exceptionKeyPrefix}last_exception`

        const exceptionsTotal = ruleStatusDetailData[totalExceptionsKey]
        const lastException = ruleStatusDetailData[lastExceptionKey]
        const res = type === 'total' ? exceptionsTotal : lastException
        return res
      }
    },

    isRuleRunning() {
      return ({ status }) => {
        return String(status).toLocaleLowerCase() === 'running'
      }
    },
    isStopRuleSuccess() {
      return ({ status }) => {
        return String(status).toLocaleLowerCase().includes('canceled manually')
      }
    },
    isSelectedRules() {
      return this.selectedRules.length
    },
  },

  watch: {
    statusVisisble(val) {
      if (!val) {
        clearTimeout(this.timer)
      }
    },
  },

  created() {
    this.initData()
  },

  beforeRouteLeave(from, to, next) {
    clearInterval(this.runningRulesStatusTimer)
    next()
  },

  methods: {
    async getRuleList() {
      try {
        const rules = await loadRules(this.nodeID)
        this.records =
          rules?.data.map((item) => {
            return {
              ...item,
              isRunning: item.status === 'Running',
            }
          }) || []
        return Promise.resolve(this.records)
      } catch (error) {
        this.records = []
        return Promise.reject(error)
      }
    },

    async initData() {
      this.loading = true
      try {
        await this.getRuleList()

        const runningRules = this.records.filter((item) => item.status === 'Running')
        if (runningRules.length) {
          this.records = await this.getAllRunningRuleStatusData(this.records)
          await this.loopGetRunningRulesStatus()
        }
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Get all running rule status data
     * @param {Array} rules all rules
     */
    getAllRunningRuleStatusData(rules) {
      if (!rules) return []
      if (!rules.length) return rules

      const ruleList = cloneDeep(rules)

      return new Promise((resolve, reject) => {
        const runningRules = ruleList.filter((item) => item.status.includes('Running'))
        if (!runningRules.length) {
          resolve(ruleList)
        }

        const requestList = runningRules.map((item) => viewRuleStatus(this.nodeID, item.id))
        Promise.allSettled(requestList)
          .then((res) => {
            for (let i = 0; i < res.length; i += 1) {
              const statusDetail = res[i]?.value?.data
              const rule = runningRules[i]
              const index = ruleList.findIndex((item) => item.id === rule.id)
              ruleList[index].statusDetail = statusDetail || {}
            }
            resolve(ruleList)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    loopGetRunningRulesStatus() {
      this.runningRulesStatusTimer = setInterval(() => {
        this.getAllRunningRuleStatusData(this.records).then((res) => {
          this.records = res
        })
      }, 30000)
    },

    goToSubApp() {
      this.$router.push({
        path: '/flowEditor/flow',
      })
    },

    goToRuleDetail(id, oper) {
      this.$router.push({
        path: `/nodes/${this.nodeID}/rules/${id}`,
        query: { oper },
      })
    },
    goToRuleFlowDetail(data, oper) {
      const { name, id } = data
      this.$router.push({
        path: '/flowEditor/flow',
        query: { name, id, oper },
      })
    },
    async showDetail(row, oper) {
      const { id } = row
      try {
        // TODO: rule list return a flag to identity if it is flow
        const { data: ruleDetail } = await viewRule(this.nodeID, id)
        const { graph } = ruleDetail
        if (graph) {
          this.goToRuleFlowDetail(row, oper)
        } else {
          this.goToRuleDetail(id, oper)
        }
      } catch (error) {
        this.goToRuleDetail(id, oper)
        console.log('error', error)
      }
    },
    remove({ id }) {
      deleteRule(this.nodeID, id)
        .then((res) => {
          if (res.status === 200 && res.statusText === 'OK') {
            this.$Message.success(res.data)
          } else {
            this.$Message.success(this.$t('common.deleteSuccess'))
          }
          this.initData()
        })
        .catch(() => {})
    },

    restartRuleById({ id }) {
      this.$set(this.isLoadingStatusSet, id, true)
      this.loadingStatusText = this.$t('common.restarting')
      restartRule(this.nodeID, id)
        .then((res) => {
          if (res.status === 200 && res.statusText === 'OK') {
            this.$Message.success(res.data)
          } else {
            this.$Message.success(this.$t('common.restartSuccess'))
          }
          this.initData()
        })
        .finally(() => {
          this.$set(this.isLoadingStatusSet, id, false)
          this.loadingStatusText = ''
        })
    },

    startRuleById(id) {
      this.$set(this.isLoadingStatusSet, id, true)
      this.loadingStatusText = this.$t('common.starting')
      startRule(this.nodeID, id)
        .then((res) => {
          if (res.status === 200 && res.statusText === 'OK') {
            this.$Message.success(res.data)
          } else {
            this.$Message.success(this.$t('common.startSuccess'))
          }
          this.initData()
        })
        .finally(() => {
          this.$set(this.isLoadingStatusSet, id, false)
          this.loadingStatusText = ''
        })
    },

    stopRuleById(id) {
      this.$set(this.isLoadingStatusSet, id, true)
      this.loadingStatusText = this.$t('common.stopping')
      stopRule(this.nodeID, id)
        .then((res) => {
          if (res.status === 200 && res.statusText === 'OK') {
            this.$Message.success(res.data)
          } else {
            this.$Message.success(this.$t('common.stopSuccess'))
          }
          this.initData()
        })
        .finally(() => {
          this.$set(this.isLoadingStatusSet, id, false)
          this.loadingStatusText = ''
        })
    },

    toggleRuleStatus(rule) {
      const { id } = rule
      if (this.isRuleRunning(rule)) {
        this.stopRuleById(id)
      } else {
        this.startRuleById(id)
      }
    },

    // show rule status
    setShowStatusInterval() {
      this.timer = setTimeout(() => {
        this.showStatus({ id: this.currentRuleId })
      }, 5000)
    },
    showStatus({ id }) {
      this.statusVisisble = true
      this.currentRuleId = id
      viewRuleStatus(this.nodeID, id)
        .then((res) => {
          this.statusRecord = res.data
          this.setShowStatusInterval()
        })
        .catch(() => {})
    },

    showTopo({ id }) {
      this.$router.push({
        path: `/nodes/${this.nodeID}/rules/${id}/topo`,
      })
    },

    // copy rule
    showCopyRuleModal(row) {
      this.isCopyRuleModalVisible = true
      const { id } = row
      this.copiedRuleId = id
    },
    closeCopyRuleModal() {
      this.copiedRuleId = ''
      this.isCopyRuleModalVisible = false
    },
    submitedCopyRule() {
      this.closeCopyRuleModal()
      this.initData()
    },

    // Clear all rule alarm: record the last time the alarm was cleared
    async clearRuleAlarms() {
      const currentTimestamp = new Date().getTime()
      setClearRuleAlarmTime(currentTimestamp)
      this.initData()
    },

    // Batch export rules
    selectRuleChange(selectedRules) {
      this.selectedRules = selectedRules || []
    },
    exportSelectedRules() {
      const selectedRuleIDs = this.selectedRules.map((rule) => rule.id)
      batchExportRules(this.nodeID, selectedRuleIDs)
        .then((res) => {
          const { data, headers } = res
          const dataText = JSON.stringify(data, null, 2)
          this.downloadFile(headers, dataText, { fileName: `export rules` })
        })
        .catch((error) => {
          console.error(error)
        })
    },

    // show import rules model
    isShowImportRulesModal(val) {
      if (val === undefined || val === null) {
        this.importRulesModalVisible = false
        return
      }
      this.importRulesModalVisible = val
    },
    // Batch import rules
    importRules(data) {
      this.isImportingRules = true
      batchImportRules(this.nodeID, data)
        .then(() => {
          this.$Message.success(this.$t('common.uploadSuccess'))
          this.isShowImportRulesModal(false)
          this.initData()
        })
        .finally(() => {
          this.isImportingRules = false
        })
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/mixins.scss';

.rules {
  .ivu-icon-md-refresh-circle:hover {
    color: #ff9900;
  }
  .ivu-table-wrapper {
    overflow: inherit;
  }
  .opertor-icon {
    margin-right: 10px;
  }
}
.status-drawer {
  .view-item {
    margin-bottom: 8px !important;
  }
}
.icon-warning {
  color: #ff9900 !important;
}
.status-wrap {
  @include display-flex(flex-start, center);
  cursor: pointer;
  .status-detail {
    padding: 0 6px;
    font-size: 16px;
  }
}

.btn {
  margin-right: 10px;
}
</style>
